import {
  JobItemAuthorisationEnum,
  type JobItemAuthorisationType,
  JobStatusEnum,
  type JobStatusType,
} from 'pos-shared/models/jobSchema.ts';
import type {
  OrderJobFeModel,
  OrderScheduleDetailsModel,
} from 'pos-shared/models/orderSchema.ts';
import type { ScheduleBoardSlotItemsModel } from 'pos-shared/models/schedulingSchema.ts';
import {
  JobItemPartStatusEnum,
  type JobItemPartStatusType,
  OrderStatusEnum,
  type OrderStatusType,
} from 'pos-shared/models/statusSchema.ts';
import { literalToCamelCase } from 'pos-shared/utils/textHelpers.ts';

export const mapToJobBuilderTable = (jobs: OrderJobFeModel[]) =>
  jobs.map((job) => ({
    ...job,
    reference: job.reference?.slice(job.reference.indexOf('JOB')) ?? '-',
    description: job.items.map((item) => item.description).join(', '),
  }));

export const mapToOrderJobListForRefund = (jobs: OrderJobFeModel[]) =>
  mapToJobBuilderTable(jobs.filter((job) => job.status === 'Authorised'));

export const isJobStatus = (
  status: string | null | undefined,
  statusType: JobStatusType | JobStatusType[]
): boolean =>
  Array.isArray(statusType)
    ? statusType.includes(status as JobStatusType)
    : JobStatusEnum.enum[statusType] === status;

export const isOrderStatus = (
  status: string | null | undefined,
  statusType: OrderStatusType | OrderStatusType[]
): boolean =>
  Array.isArray(statusType)
    ? statusType.includes(status as OrderStatusType)
    : OrderStatusEnum.enum[statusType] === status;

export const isPartStatus = (
  status: string | null | undefined,
  statusType: JobItemPartStatusType | JobItemPartStatusType[]
): boolean =>
  Array.isArray(statusType)
    ? statusType.includes(status as JobItemPartStatusType)
    : JobItemPartStatusEnum.enum[statusType] === status;

export const isItemAuthorisation = (
  status: string | null | undefined,
  authType: JobItemAuthorisationType | JobItemAuthorisationType[]
): boolean =>
  Array.isArray(authType)
    ? authType.includes(status as JobItemAuthorisationType)
    : JobItemAuthorisationEnum.enum[authType] === status;

export const canAssignTechnicians = (job: OrderJobFeModel) =>
  !!job?.scheduleCustomer &&
  !isJobStatus(job.status, ['Cancelled', 'Complete']) &&
  job.fulfilmentType !== 'Mobile';

export const isJobSchedulable = (job?: Pick<OrderJobFeModel, 'items'> | null) =>
  job?.items?.some((item) => item.authorised) ?? false;

export const hasJobSchedule = (
  job?: Pick<OrderJobFeModel, 'scheduleCustomer'> | null
) => !!job?.scheduleCustomer;

export const getBookableJobs = ({
  orderJobs,
  orderSchedules,
}: {
  orderJobs: OrderJobFeModel[];
  orderSchedules: OrderScheduleDetailsModel[];
}) => {
  const scheduledJobIds = new Set(
    orderSchedules.flatMap((schedule) => schedule.jobIds ?? [])
  );

  return orderJobs.filter((job) => {
    if (!isJobSchedulable(job)) {
      return false;
    }

    return !scheduledJobIds.has(job.jobId);
  });
};

export const jobAuthorisationStatus = (job: OrderJobFeModel) => {
  const isDeclined =
    job.items?.every((i) => i.authorisation === 'Declined') ?? false;
  const isApproved = job.items?.some((i) => i.authorisation === 'Approved');
  const isJobAuthorisable =
    isApproved || !isJobStatus(job.status, ['Quoted Job', 'Cancelled']);
  return isJobAuthorisable ? 'Approved' : isDeclined ? 'Declined' : 'Pending';
};

// These are in a specific order, the item closest to the top is the 'earliest' status
// and is displayed in the top level of the job builder table
const statusOrder: JobItemPartStatusType[] = [
  'Not Ordered',
  'Ordered',
  'In Transit',
  'WIP',
  'Fulfilled',
  'Not Fitted',
  'Cancelled',
  'Full Return',
  'Partial Return',
  'Speculative',
];

export const statusPriority = Object.fromEntries(
  statusOrder.map((status, index) => [literalToCamelCase(status), index])
);

const earliestPartStatus = (statuses: (string | null | undefined)[]) =>
  statuses
    .filter((status) => status && status !== '')
    .map((status) => literalToCamelCase(status as string))
    .reduce((earliestStatus, currentStatus) => {
      if (!(currentStatus in statusPriority)) return earliestStatus;

      if (
        !earliestStatus ||
        statusPriority[currentStatus] < statusPriority[earliestStatus]
      ) {
        return currentStatus;
      }

      return earliestStatus;
    }, '');

export const jobPartStatus = (job: OrderJobFeModel) =>
  earliestPartStatus(
    job.items.flatMap((item) => item.parts?.map((part) => part.status) ?? [])
  );

export const scheduleBoardJobPartStatus = (
  items: ScheduleBoardSlotItemsModel
) =>
  earliestPartStatus(
    items.flatMap((item) => item.parts?.map((part) => part.status) ?? [])
  );
